<template>
  <div class="opportunity-meta-card">
    <div class="opportunity-meta-card__rows">
      <div class="opportunity-meta-card__row">
        <span class="opportunity-meta-card__label">{{ $t('page.opportunity-details.meta.status') }}</span>
        <div class="opportunity-meta-card__status">
          <BaseStatusMenu
            ref="opportunityStatusMenu"
            :items="opportunityStatuses"
            :current-status="opportunity.status"
            attach=".opportunity-meta-card__status"
            @change="onStatusChange"
            searchAllowed
          />
        </div>
      </div>

      <div class="opportunity-meta-card__row opportunity-meta-card__row--labels">
        <span class="opportunity-meta-card__label">{{ $t('page.opportunity-details.meta.labels') }}</span>
        <div class="opportunity-meta-card__labels">
          <BaseLabelsMenu
            :items="opportunityLabels"
            :value="labels"
            attach=".opportunity-meta-card__labels"
            showAllLabels
            @change="onLabelsChange"
          />
        </div>
      </div>

      <div class="opportunity-meta-card__row">
        <span class="opportunity-meta-card__label">{{ $t('page.opportunity-details.meta.assigned-to') }}</span>
        <div class="opportunity-meta-card__assignees">
          <BaseAssignation
            :value="assignees"
            @change="(userIds) => onAssign(userIds)"
            attach=".opportunity-meta-card__assignees"
            tooltipPlacement="bottom"
          />
        </div>
      </div>

      <div v-if="tenantHasGonogoAccess()" class="opportunity-meta-card__row">
        <span class="opportunity-meta-card__label">{{ $t('page.opportunity-details.meta.go-nogo') }}</span>
        <div class="opportunity-meta-card__go-nogo">
          <div v-if="finalDecisionDisplayVote" class="opportunity-meta-card__final-decision" @click="openGoNogo">
            <v-popover
              placement="top"
              trigger="hover"
              popoverClass="opportunity-meta-card__vote-tooltip"
              :delay="{ show: 600, hide: 0 }"
            >
              <template v-slot:popover>
                <OpportunityVoteTooltipDetails
                  :name="getUserFullName(opportunity.finalDecision.decidedBy)"
                  :vote-type="$t('go-nogo.final-decision.header-tag-tooltip')"
                  :reasons="finalDecisionDetails"
                />
              </template>
              <BaseVoteTicket :name="finalDecisionVoteLabel" :vote="finalDecisionDisplayVote" is-final />
            </v-popover>
          </div>
          <div v-if="votes.length > 0" class="opportunity-meta-card__votes" @click="openGoNogo">
            <v-popover
              v-for="(vote, index) in sortedVotes.slice(0, 3)"
              :key="index"
              placement="top"
              trigger="hover"
              popoverClass="opportunity-meta-card__vote-tooltip"
              :delay="{ show: 600, hide: 0 }"
            >
              <BaseVoteTicket :name="getUserFullName(vote.userId)" :vote="vote.vote" />
              <template v-slot:popover>
                <OpportunityVoteTooltipDetails
                  :name="getUserFullName(vote.userId)"
                  :vote-type="getVoteTypeLabel(vote)"
                  :reasons="getVoteReasonsLabel(vote)"
                />
              </template>
            </v-popover>
            <v-popover
              v-if="votes.length > 3"
              placement="top"
              trigger="hover"
              popoverClass="opportunity-meta-card__vote-tooltip"
            >
              <div class="base-assignation__more-people">+{{ votes.length - 3 }}</div>
              <template v-slot:popover>
                <div class="opportunity-meta-card__vote-tooltip-content">
                  <div
                    v-for="(vote, index) in sortedVotes.slice(3)"
                    :key="index"
                    class="opportunity-meta-card__vote-tooltip-item"
                  >
                    <OpportunityVoteTooltipDetails
                      :name="getUserFullName(vote.userId)"
                      :vote-type="getVoteTypeLabel(vote)"
                      :reasons="getVoteReasonsLabel(vote)"
                    />
                    <BaseVoteTicket :name="getUserFullName(vote.userId)" :vote="vote.vote" />
                  </div>
                </div>
              </template>
            </v-popover>
          </div>
          <div
            v-else-if="!finalDecisionDisplayVote"
            class="opportunity-meta-card__vote-action"
            role="button"
            tabindex="0"
            @click="openVoteDialog"
            @keydown.enter.prevent="openVoteDialog"
            @keydown.space.prevent="openVoteDialog"
          >
            <BaseIcon name="vote" />
            {{ $t('page.opportunities.vote') }}
          </div>
        </div>
      </div>
    </div>

    <GoNoGoDialog
      ref="goNoGoDialog"
      :userVote="currentUserVote"
      :changeVote="currentUserVote && currentUserVote.isPending ? false : !!currentUserVote"
      :opportunityId="opportunity.id"
    />
  </div>
</template>

<script>
import BaseAssignation from '@/components/base/BaseAssignation';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLabelsMenu from '@/components/base/BaseLabelsMenu.vue';
import BaseStatusMenu from '@/components/base/BaseStatusMenu.vue';
import BaseVoteTicket from '@/components/base/BaseVoteTicket';
import GoNoGoDialog from '@/components/page-go-nogo/GONoGoDialog.vue';
import OpportunityVoteTooltipDetails from '@/components/page-opportunities/OpportunityVoteTooltipDetails.vue';
import { tenantHasGonogoAccess } from '@/util/module-access-util';
import { toFinalDecisionDisplayVote } from '@/util/go-nogo-final-decision';
import { normalizeVoteReasonNewlines } from '@/util/go-nogo-vote-reasons';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'OpportunityMetaCard',
  components: {
    BaseAssignation,
    BaseIcon,
    BaseLabelsMenu,
    BaseStatusMenu,
    BaseVoteTicket,
    GoNoGoDialog,
    OpportunityVoteTooltipDetails,
  },
  props: {
    opportunityId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      votesLoadedForId: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenant', ['users']),
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('opportunityStatuses', ['opportunityStatuses']),
    ...mapGetters('opportunityLabels', ['opportunityLabels']),
    ...mapGetters('goNoGo', ['opportunityVotes']),
    assignees() {
      return (this.opportunity && this.opportunity.assignees) || [];
    },
    labels() {
      return (this.opportunity && this.opportunity.labels) || [];
    },
    votes() {
      if (this.opportunity && this.votesLoadedForId === this.opportunity.id) {
        return this.opportunityVotes || [];
      }
      return (this.opportunity && this.opportunity.votes) || [];
    },
    currentUserVote() {
      if (!this.currentUser) return null;
      return this.votes.find((vote) => vote.userId === this.currentUser.id) || null;
    },
    sortedVotes() {
      return [...this.votes].sort((a, b) => {
        const nameA = this.getUserFullName(a.userId);
        const nameB = this.getUserFullName(b.userId);
        return nameA.localeCompare(nameB);
      });
    },
    finalDecisionDisplayVote() {
      const finalDecision = this.opportunity && this.opportunity.finalDecision;
      return finalDecision ? toFinalDecisionDisplayVote(finalDecision.vote) : null;
    },
    finalDecisionVoteLabel() {
      return this.finalDecisionDisplayVote ? this.$t(`go-nogo.${this.finalDecisionDisplayVote.toLowerCase()}`) : '';
    },
    finalDecisionDetails() {
      const finalDecision = this.opportunity && this.opportunity.finalDecision;
      if (!finalDecision) return null;
      const parts = [...(finalDecision.reasons || [])];
      if (finalDecision.comment) {
        parts.push(normalizeVoteReasonNewlines(finalDecision.comment));
      }
      return parts.length ? parts.join(', ') : null;
    },
  },
  watch: {
    users: {
      immediate: true,
      handler() {
        this.loadVotes();
      },
    },
    'opportunity.id'() {
      this.votesLoadedForId = null;
      this.loadVotes();
      this.getOpportunityStatuses();
      this.getOpportunityLabels();
    },
  },
  methods: {
    ...mapActions('opportunity', ['editOpportunity']),
    ...mapActions('opportunityStatuses', ['getOpportunityStatuses']),
    ...mapActions('opportunityLabels', ['getOpportunityLabels']),
    ...mapActions('goNoGo', ['fetchOpportunityVotes', 'setCurrentUser']),
    tenantHasGonogoAccess,
    isGoVote(vote) {
      return vote === 'YES' || vote === 'GO';
    },
    isNogoVote(vote) {
      return vote === 'NO' || vote === 'NOGO';
    },
    onLabelsChange(labelIds) {
      return this.$store
        .dispatch('opportunity/assignLabels', {
          opportunityId: this.opportunityId,
          labelIds,
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    onAssign(userIds) {
      return this.$store
        .dispatch('opportunity/assign', {
          opportunityId: this.opportunityId,
          userIds,
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    onStatusChange(newStatus) {
      this.$refs.opportunityStatusMenu.setLoading(true);
      const body = {
        name: this.opportunity.name,
        evaluationGridTemplateId: null,
        statusId: newStatus ? newStatus.id : null,
        startDate: this.opportunity.startDate,
        endDate: this.opportunity.endDate,
        source: this.opportunity.source,
        emitter: this.opportunity.emitter,
        notes: this.opportunity.notes,
        version: this.opportunity.version,
      };
      return this.editOpportunity({
        opportunityId: this.opportunity.id,
        opportunity: body,
      }).finally(() => this.$refs.opportunityStatusMenu.setLoading(false));
    },
    async loadVotes() {
      if (!tenantHasGonogoAccess() || !this.opportunity || !this.users || !this.currentUser) return;
      const opportunityId = this.opportunity.id;
      await this.fetchOpportunityVotes({
        opportunityId,
        users: this.users,
        currentUser: this.currentUser,
      });
      this.votesLoadedForId = opportunityId;
    },
    openGoNogo() {
      this.$router.push({
        name: 'opportunity-go-nogo',
        params: { opportunityId: this.opportunity.id },
      });
    },
    openVoteDialog() {
      this.setCurrentUser(this.currentUser);
      this.$refs.goNoGoDialog.openDialog();
    },
    getVoteTypeLabel(vote) {
      if (vote.isPending || !vote.vote) {
        return this.$t('go-nogo.pending');
      }
      if (this.isGoVote(vote.vote)) {
        return this.$t('go-nogo.dialog.go');
      }
      if (this.isNogoVote(vote.vote)) {
        return this.$t('go-nogo.dialog.no-go');
      }
      if (vote.vote === 'NEUTRAL' || vote.vote === 'INDIFFERENT') {
        return this.$t('go-nogo.dialog.indifferent');
      }
      return vote.vote;
    },
    getVoteReasonsLabel(vote) {
      if (vote.transformedReasons?.length) {
        return vote.transformedReasons
          .map((reason) => (typeof reason === 'string' ? reason : reason?.reason))
          .filter(Boolean)
          .join(', ');
      }
      if (vote.reasons?.length) {
        return vote.reasons.join(', ');
      }
      return null;
    },
    getUserFullName(userId) {
      if (!this.users) return this.$t('unknown.user');
      const user = this.users.find((item) => item.id === userId);
      return user ? user.fullName : this.$t('unknown.user');
    },
  },
  created() {
    this.getOpportunityStatuses();
    this.getOpportunityLabels();
  },
};
</script>

<style lang="scss">
.opportunity-meta-card {
  padding: 32px;
  border-radius: $border-radius-root;
  background: $color-white;

  &__rows {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    padding: 8px 8px 16px 0;
    border-bottom: 1px solid $color-border;

    &:last-child {
      border-bottom: 0;
      padding-bottom: 0;
    }
  }

  // Labels wrap onto several lines, so they keep their own column beside the
  // row title instead of wrapping underneath it, and start at its top.
  &__row--labels {
    align-items: flex-start;
    flex-wrap: nowrap;
    gap: 16px;

    // Offset the title by the badge's own top padding so the two texts sit on
    // the same baseline.
    .opportunity-meta-card__label {
      padding-top: 4px;
    }
  }

  &__label {
    flex-shrink: 0;
    font-size: $font-size-small;
    font-weight: 600;
    color: $color-normal-pale;
    min-width: 80px;
  }

  &__go-nogo,
  &__assignees,
  &__labels,
  &__status {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }

  &__labels {
    flex: 1;
    min-width: 0;
  }

  &__vote-action {
    @include inline-add-button;
    gap: 6px;

    .base-icon {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }
  }

  // The rows are right-aligned, so pull back the buttons' own padding to line
  // their text up with the badges and avatars of the neighbouring rows.
  &__go-nogo,
  &__assignees,
  &__labels {
    .base-assignation__add,
    .base-labels-menu__add,
    .opportunity-meta-card__vote-action {
      margin-right: -8px;
    }
  }

  // BaseMenu forces top/left: auto, which does not resolve to the attach
  // element's top inside this flex layout. Pin the dropdown to that top.
  &__assignees,
  &__labels,
  &__status {
    position: relative;
    overflow: visible;

    .v-menu__content.base-menu {
      position: absolute !important;
      top: 0 !important;
      left: auto !important;
      right: 0;
    }
  }

  &__final-decision {
    display: flex;
    width: min-content;
    cursor: pointer;

    .base-tag {
      text-transform: uppercase;
      font-weight: $font-weight-bold;
    }
  }

  &__votes {
    display: flex;
    align-items: center;
    gap: 2px;
    cursor: pointer;
  }

  &__vote-tooltip {
    padding: 8px;
    background-color: $color-normal !important;
    color: $color-white !important;

    .popover-inner,
    .tooltip-inner {
      padding: 0;
    }

    .popover-arrow {
      background-color: $color-normal !important;
    }
  }

  &__vote-tooltip-item {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: center;
  }

  &__vote-tooltip-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}
</style>
